/**
 * O que a TELA diz quando a partida para. Modelo puro, sem DOM e sem React.
 *
 * Existe porque o cliente ficava MUDO no fim da partida. O servidor encerra em
 * `matchDurationTicks`, manda `reject` com `matchOver` e fecha o socket — tudo
 * certo do lado de lá. O cliente guardava o motivo numa variável que só o
 * `debug-api` lia (desligado por padrão), escrevia um `console.warn` que jogador
 * nenhum vê, e seguia desenhando a 60 fps um mundo congelado.
 *
 * MEDIDO no aparelho real: `tick` parado em 5400 por 100 s seguidos, com 74
 * toques do jogador contra o jogo morto. O que se sentia como "o ímã trava e o
 * andar trava, impossível de jogar" era uma partida ACABADA sem aviso — e foi
 * essa mudez que custou `DIS-46`, `DIS-48` e as três hipóteses do `T-XPR`.
 *
 * NEVER decida o texto lendo a string de motivo: a string é log, e log muda. O
 * dono da decisão é o `code`, que é contrato de protocolo.
 */
import { REJECT_CODES } from '@game/protocol';

/**
 * `won` é o fim que o jogo INTEIRO existe para produzir, e por isso ele NEVER
 * divide entrada com `over`: os dois acabam a partida e dizem coisas opostas ao
 * jogador. `over` é o fim normal sem vitória — o tempo acabou —, e é o outro que
 * NEVER é falha. `lost` pede tentar de novo, e `blocked` NEVER adianta tentar:
 * é o cliente que está errado.
 */
export type MatchEndKind = 'won' | 'over' | 'lost' | 'blocked';

export interface MatchEndView {
  readonly kind: MatchEndKind;
  /** A frase grande. Fala do JOGO, NEVER do socket. */
  readonly title: string;
  /** A linha pequena, com a causa em português de jogador. */
  readonly detail: string;
  /** Mostrar a ação de jogar de novo. `blocked` NEVER mostra: reconectar repete o erro. */
  readonly canRetry: boolean;
  /**
   * O motivo cru, para o relato de teste e para o `debug-api`. NEVER vai para a
   * frase grande: "servidor recusou (6)" é texto de quem escreve o servidor.
   */
  readonly reason: string;
}

const BY_CODE: Readonly<Record<number, Omit<MatchEndView, 'reason'>>> = {
  [REJECT_CODES.matchWon]: {
    kind: 'won',
    // Diz o que o GRUPO fez, e NEVER "você venceu": o jogo é cooperativo de 2 a 4
    // (`ADR-0013`), e a mesma frase tem que servir para quem jogou sozinho e para
    // quem carregou o Núcleo a quatro.
    title: 'Núcleo extraído',
    detail: 'vocês tiraram o Núcleo antes do colapso',
    canRetry: true,
  },
  [REJECT_CODES.matchOver]: {
    kind: 'over',
    title: 'partida encerrada',
    detail: 'o tempo acabou',
    canRetry: true,
  },
  [REJECT_CODES.roomFaulted]: {
    kind: 'lost',
    title: 'a sala caiu',
    detail: 'a partida foi interrompida',
    canRetry: true,
  },
  [REJECT_CODES.roomFull]: {
    kind: 'blocked',
    title: 'sala cheia',
    detail: 'não há vaga nesta partida',
    canRetry: false,
  },
  [REJECT_CODES.protocolTooOld]: {
    kind: 'blocked',
    title: 'versão antiga',
    detail: 'recarregue a página para atualizar',
    canRetry: false,
  },
  [REJECT_CODES.protocolTooNew]: {
    kind: 'blocked',
    title: 'versão antiga',
    detail: 'recarregue a página para atualizar',
    canRetry: false,
  },
  [REJECT_CODES.rateLimited]: {
    kind: 'lost',
    title: 'conexão interrompida',
    detail: 'comandos demais em pouco tempo',
    canRetry: true,
  },
  [REJECT_CODES.badInput]: {
    kind: 'lost',
    title: 'conexão interrompida',
    detail: 'o servidor recusou um comando',
    canRetry: true,
  },
};

/**
 * Sem `code` é queda de REDE — o caso mais comum em celular, e o único em que o
 * servidor NEVER decidiu nada. Também é o destino de todo código futuro que este
 * build ainda não conhece: `REJECT_CODES` cresce sem subir `PROTOCOL_VERSION`
 * (ver `schema.ts`), então cliente antigo recebendo código novo é o caminho
 * NORMAL, e NEVER um erro. Ele cai aqui e oferece tentar de novo, que é a coisa
 * certa a oferecer quando não se sabe o que houve.
 */
const FALLBACK: Omit<MatchEndView, 'reason'> = {
  kind: 'lost',
  title: 'conexão perdida',
  detail: 'verifique sua internet e tente de novo',
  canRetry: true,
};

export function matchEndView(reason: string, code: number | undefined): MatchEndView {
  const found = code === undefined ? undefined : BY_CODE[code];
  return { ...(found ?? FALLBACK), reason };
}
